"use client";

import React from "react";
import { Cpu, Crosshair, Users } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  title: "Surface Grading Services",
  description: "Professional site preparation and precision grading to ensure a perfectly level foundation for your construction projects.",
  icon: Cpu
}, {
  title: "Equipment Rentals",
  description: "Access a wide range of high-performance machinery, from skid steers to compact backhoes, with flexible rental terms.",
  icon: Crosshair
}, {
  title: "Rapid Deployment",
  description: "Get the equipment you need when you need it. Our logistics network ensures on-time delivery to keep your schedule on track.",
  icon: Users
}];
function PillarCard({
  pillar,
  index
}: {
  pillar: typeof pillars[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = pillar.icon;
  return <div data-section-id="119358" ref={ref} data-index={index} className={`transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full bg-card border-border relative overflow-hidden group hover:bg-muted/10 hover:border-primary hover:shadow-lg transition-all duration-500 rounded-sm flex flex-col">
        {}
        <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.1)_50%)] bg-[length:100%_4px] z-0 mix-blend-overlay" />
        
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-muted/5 z-0 pointer-events-none" />

        <CardHeader className="relative z-10 pb-4 flex-none">
          <div className="mb-6 inline-flex p-4 rounded-sm bg-muted/20 border border-border group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors duration-500">
            <Icon className="w-8 h-8 text-primary transform group-hover:scale-110 transition-transform duration-500" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tighter uppercase text-card-foreground font-sans">
            {pillar.title}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative z-10 flex-grow">
          <CardDescription className="text-base text-muted-foreground leading-relaxed font-sans">
            {pillar.description}
          </CardDescription>
        </CardContent>

        {}
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </Card>
    </div>;
}
export default function StrategicPillars() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-contractors-advantage" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-96 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-6 uppercase font-sans text-foreground drop-shadow-sm">
            THE CONTRACTOR'S <span className="text-primary">ADVANTAGE</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans max-w-2xl mx-auto">
            Expert surface grading services and flexible equipment rentals to keep your construction projects moving forward without interruption.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {pillars.map((pillar, i) => <PillarCard key={i} pillar={pillar} index={i} />)}
        </div>
      </div>
    </section>;
}